import { PageRef } from 'providers/page-ref';

/**
 * The "leave the page to take a screenshot, then come back" round trip, with one owner.
 *
 * Two pages join this trip today — the Send Feedback page and the Send Coach Message
 * page. Both let the user attach a picture of their own screen to the message they are
 * writing, and taking that picture means navigating AWAY from the page, because the
 * capture photographs whatever is behind it. So the text the user has already typed
 * cannot stay on the page: it has to be held somewhere that outlives the page, and
 * handed back when the same page is re-entered.
 *
 * Every rule below used to be re-derived by hand in each page, and each rule is
 * invisible when it is broken — the page simply comes back empty, or shows text the user
 * typed on the OTHER page. That is what happened before this class existed, so the rules
 * live here, once, with a unit test beside them:
 *
 * - the text is handed back ONLY to the page that stashed it, because both pages share
 *   one stash and either can be opened straight from the settings menu while the other's
 *   stash is still standing;
 * - the text is handed back whether or not the capture produced an image. A capture that
 *   failed still comes back to the page, and reading the stash only when an image existed
 *   threw away everything the user had written;
 * - an empty string is a real stash. A page that stashed nothing typed still owns a trip,
 *   so "nothing was stashed" has to be a different answer from "an empty string was
 *   stashed";
 * - each value is cleared as it is read, so a later, unrelated visit to the page does not
 *   pick up a stale stash.
 *
 * The stash can also hold the Sentry id of the error a feedback report is about, under the
 * same rules as the text. The page that comes back is a NEW page, pushed by the camera
 * button, which knows nothing about that error. Without the stash the report was sent
 * with no link to the error that prompted it (#3255).
 */
export class ScreenshotTrip {
  private stashedText: string | undefined = undefined;
  private stashedErrorId: string | undefined = undefined;
  private stashOwner: PageRef<any, any> | undefined = undefined;
  private returnPage: PageRef<any, any> | undefined = undefined;
  private capturedImage: string | undefined = undefined;

  /**
   * The page is leaving to take a screenshot: keep its typed text, and the id of the
   * error it is reporting if it has one, and remember that both belong to this page so
   * no other page can pick them up.
   *
   * This does NOT say where to come back to — see `returnTo`, which not every page wants.
   */
  stashText(page: PageRef<any, any>, typedText: string, errorId?: string): void {
    this.stashedText = typedText;
    this.stashedErrorId = errorId;
    this.stashOwner = page;
  }

  /**
   * After the capture, navigate back to this page, with no parameters.
   *
   * Only a page that wants THAT asks for it. The Send Feedback page deliberately does
   * not: when no page has asked to be returned to, the app pushes the feedback page
   * itself, with the kind of feedback this is. That push does NOT carry the error id the
   * user is reporting; the page gets that back from the stash (see `takeStashFor`).
   */
  returnTo(page: PageRef<any, any>): void {
    this.returnPage = page;
  }

  /**
   * The page the capture should return to, or undefined when no page asked to be
   * returned to. Cleared as it is read, so one trip sends the user back exactly once.
   */
  takeReturnPage(): PageRef<any, any> | undefined {
    const page = this.returnPage;
    this.returnPage = undefined;
    return page;
  }

  /**
   * The capture finished. Pass the image, or undefined when the capture failed — the
   * pages read a failed capture as "come back to the plain form", not as "no trip".
   */
  recordCapture(image: string | undefined): void {
    this.capturedImage = image;
  }

  /**
   * Whether an image is waiting to be drawn. This only looks; it does not clear, because
   * the page asks this question while deciding which form to show, well before the
   * canvas exists to draw on.
   */
  get hasScreenshot(): boolean {
    return !!this.capturedImage;
  }

  /** The captured image, cleared as it is read. */
  takeScreenshot(): string | undefined {
    const image = this.capturedImage;
    this.capturedImage = undefined;
    return image;
  }

  /**
   * The text this page stashed on its way out, cleared as it is read. Undefined when
   * there is no stash, and undefined when the standing stash belongs to another page.
   */
  takeTextFor(page: PageRef<any, any>): string | undefined {
    return this.takeStashFor(page)?.text;
  }

  /**
   * The text AND the error id this page stashed on its way out, cleared as they are read.
   * Undefined when there is no stash, and undefined when the standing stash belongs to
   * another page. `errorId` is undefined when the page stashed none.
   */
  takeStashFor(page: PageRef<any, any>): { text: string, errorId: string | undefined } | undefined {
    if (this.stashOwner !== page || this.stashedText === undefined) {
      return undefined;
    }
    const stash = { text: this.stashedText, errorId: this.stashedErrorId };
    this.stashedText = undefined;
    this.stashedErrorId = undefined;
    this.stashOwner = undefined;
    return stash;
  }

  /**
   * The trip is over, or the user walked away from it: drop the stashed text, the stashed
   * error id and the page to return to.
   *
   * A captured image is deliberately left alone. Both pages abandon the trip on every
   * entry, and a page that is showing the screenshot editor has already taken the image
   * for itself by then — but a page that is NOT showing it has deliberately left the
   * image standing, and clearing it here would throw away a capture the user just asked
   * for before anything had a chance to draw it.
   *
   * Dropping the return page matters as much as dropping the text: left standing, a trip
   * the user started on one page and walked away from would send the NEXT capture back
   * to that page, where the text now stashed belongs to a different page, so it would be
   * refused and lost.
   */
  abandon(): void {
    this.stashedText = undefined;
    this.stashedErrorId = undefined;
    this.stashOwner = undefined;
    this.returnPage = undefined;
  }
}
